import { makeStyles, MenuItem, Popover } from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import React from "react";

import ConfirmButton from "../ConfirmButton";
import CustomTooltip from "../CustomTooltip";

const useStyles = makeStyles(
  _ => ({
    arrowDown: {
      borderLeft: "10px solid transparent",
      borderRight: "10px solid transparent",
      borderTop: "10px solid #fff",
      bottom: -10,
      height: 0,
      position: "absolute",
      right: 10,
      width: 0,
      zIndex: 2
    },
    arrowUp: {
      borderBottom: "10px solid #fff",
      borderLeft: "10px solid transparent",
      borderRight: "10px solid transparent",
      height: 0,
      position: "absolute",
      right: 10,
      top: -10,
      width: 0,
      zIndex: 2
    },
    bulkActions: {
      "& span": {
        color: "#0030FF"
      },
      "&:hover": {
        backgroundColor: "#F4F8FF",
        border: "none",
        color: "#0030FF"
      },
      backgroundColor: "#F4F8FF",
      border: "none",
      borderRadius: "8px",
      color: "#0030FF",
      fontWeight: "bolder"
    },
    header: {
      color: "#BEBEBE",
      fontSize: 10,
      fontWeight: 600,
      padding: "0 16px",
      textTransform: "uppercase"
    },
    menu: {
      backgroundColor: "#fff",
      borderRadius: 10,
      boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
      maxWidth: 220,
      minWidth: 200,
      overflow: "visible",
      padding: "8px 0",
      position: "relative"
    },
    menuItem: {
      borderRadius: "0",
      fontWeight: 500,
      padding: "2px 17px !important",
      width: "100%",
      ...{
        "&:hover": {
          backgroundColor: "#0630ff !important",
          color: "#FFF",
          fontWeight: "bold"
        }
      }
    },
    menuItemDisabled: {
      borderRadius: "0",
      fontWeight: 500,
      opacity: "50%",
      padding: "2px 17px !important",
      ...{
        "&:hover": {
          opacity: "50%"
        }
      }
    },
    popoverPaper: {
      background: "transparent",
      borderRadius: 10,
      maxWidth: 220,
      minWidth: 200,
      overflow: "visible",
      padding: "16px 0",
      position: "relative"
    }
  }),
  { name: "ButtonActions" }
);

export interface ButtonActionItem {
  id: string;
  label: string;
  disabled?: boolean;
  tooltip?: string;
  header?: string;
  action: () => void;
}

export interface ButtonActionsProps {
  id: string;
  btnText: string;
  className?: string;
  disabled?: boolean;
  items: ButtonActionItem[];
}

const ButtonActions: React.FC<ButtonActionsProps> = props => {
  const { id, btnText, className, disabled, items } = props;
  const classes = useStyles(props);

  const [anchorModal, setAnchorModal] = React.useState(null);
  const openModal = Boolean(anchorModal);
  const [isNearBottom, setIsNearBottom] = React.useState(false);

  const groupByHeader = (
    _items: ButtonActionItem[]
  ): { [key: string]: ButtonActionItem[] } =>
    _items.reduce((acc, item) => {
      const key = item.header || "";
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(item);
      return acc;
    }, {} as { [key: string]: ButtonActionItem[] });

  const groupedItems = groupByHeader(items);

  const handleClose = () => {
    setAnchorModal(null);
  };

  const handleOpen = event => {
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;

    setIsNearBottom(spaceBelow < 200);

    setAnchorModal(event.currentTarget);
  };

  const getMenuItemComponent = (item: ButtonActionItem, index: number) => [
    <MenuItem
      id={"buttonActionsMenuItem" + index}
      className={item?.disabled ? classes.menuItemDisabled : classes.menuItem}
      key={item.id}
      onClick={() => {
        if (item?.disabled) {
          return;
        }
        item.action();
        handleClose();
      }}
    >
      <span>{item.label}</span>
    </MenuItem>
  ];

  return (
    <div className={className} id={id}>
      <ConfirmButton
        id="buttonActionsButton"
        transitionState="default"
        variant="outlined"
        className={classes.bulkActions}
        disabled={disabled}
        onClick={handleOpen}
      >
        {btnText}
        <KeyboardArrowDownIcon color="primary" />
      </ConfirmButton>

      <Popover
        id="buttonActionsPopover"
        open={openModal}
        anchorEl={anchorModal}
        onClose={() => setAnchorModal(null)}
        className={classes.popoverPaper}
        anchorOrigin={{
          horizontal: "right",
          vertical: isNearBottom ? "top" : "bottom"
        }}
        transformOrigin={{
          horizontal: "right",
          vertical: isNearBottom ? "bottom" : "top"
        }}
        elevation={0}
        PaperProps={{
          style: {
            boxShadow: "0px -2px 12px rgba(0,0,0,0.2)",
            marginBottom: isNearBottom ? 10 : 0,
            marginTop: isNearBottom ? 0 : 10,
            overflow: "visible"
          }
        }}
      >
        <div className={classes.menu}>
          <div className={isNearBottom ? classes.arrowDown : classes.arrowUp} />

          {Object.entries(groupedItems).map(([header, items]) => (
            <div key={header}>
              {header !== "" && <div className={classes.header}>{header}</div>}

              {items.map((item, index) => (
                <div key={item.id}>
                  {item?.disabled && item?.tooltip ? (
                    <CustomTooltip text={item?.tooltip} bgColor="#002972">
                      {getMenuItemComponent(item, index)}
                    </CustomTooltip>
                  ) : (
                    getMenuItemComponent(item, index)
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>
      </Popover>
    </div>
  );
};
ButtonActions.displayName = "ButtonActions";
export default ButtonActions;
